import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Ban, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";

interface Props {
  profile: {
    user_id: string;
    full_name: string | null;
    blocked_at: string | null;
    block_reason: string | null;
  };
  onChange: (blocked_at: string | null, block_reason: string | null) => void;
}

const BlockClientCard = ({ profile, onChange }: Props) => {
  const [reason, setReason] = useState(profile.block_reason || "");
  const [busy, setBusy] = useState(false);
  const isBlocked = !!profile.blocked_at;

  const handleBlock = async () => {
    setBusy(true);
    const now = new Date().toISOString();
    const trimmed = reason.trim() || null;
    const { error } = await supabase
      .from("profiles")
      .update({ blocked_at: now, block_reason: trimmed })
      .eq("user_id", profile.user_id);
    setBusy(false);
    if (error) {
      toast.error("Failed to block client", { description: error.message });
      return;
    }
    onChange(now, trimmed);
    toast.success("Client has been blocked from booking");
  };

  const handleUnblock = async () => {
    setBusy(true);
    const { error } = await supabase
      .from("profiles")
      .update({ blocked_at: null, block_reason: null })
      .eq("user_id", profile.user_id);
    setBusy(false);
    if (error) {
      toast.error("Failed to unblock client", { description: error.message });
      return;
    }
    setReason("");
    onChange(null, null);
    toast.success("Client unblocked");
  };

  return (
    <Card className={isBlocked ? "border-destructive/50" : undefined}>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          {isBlocked ? (
            <Ban size={18} className="text-destructive" />
          ) : (
            <ShieldCheck size={18} />
          )}
          Booking Access
          {isBlocked && (
            <Badge variant="destructive" className="ml-2 text-[10px] py-0 px-1.5">
              Blocked
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {isBlocked ? (
          <>
            <p className="text-sm text-muted-foreground">
              This client is blocked from creating new bookings since{" "}
              <span className="text-foreground">
                {format(parseISO(profile.blocked_at!), "MMM d, yyyy 'at' h:mm a")}
              </span>
              .
            </p>
            {profile.block_reason && (
              <div className="text-sm">
                <span className="text-muted-foreground">Reason: </span>
                <span className="text-foreground">{profile.block_reason}</span>
              </div>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleUnblock}
              disabled={busy}
              className="gap-2"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
              Unblock client
            </Button>
          </>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              Block this client from creating new bookings. Existing appointments are not affected.
            </p>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Optional reason (visible only to admins)"
              rows={2}
              className="text-sm"
            />
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm" disabled={busy} className="gap-2">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />}
                  Block client
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    Block {profile.full_name || "this client"} from booking?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    They will see a message preventing them from creating new appointments
                    until you unblock them. This does not cancel any existing bookings.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleBlock}>Block client</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default BlockClientCard;
